<div class="w-full max-w-6xl mx-auto p-6">
  <div class="mb-6">
    <h1 class="text-3xl font-bold text-slate-900 mb-2">Perfil</h1>
    <p class="text-slate-600">Consulta tus datos y gestiona tu contraseña</p>
  </div>

  <p-card class="grid gap-8">
    <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
      <p-tablist>
        @for (tab of tabs; track tab.value) {
          <p-tab [value]="tab.value" class="flex items-center gap-2! text-inherit">
            <i [class]="tab.icon"></i>
            <span>{{ tab.label }}</span>
          </p-tab>
        }
      </p-tablist>
      <p-tabpanels>
        <p-tabpanel value="generales">
          <div class="mt-4 mb-4">
            <h3 class="text-lg font-semibold text-slate-900">Información personal</h3>
            <p class="text-sm text-slate-500">Datos de tu cuenta de usuario.</p>
          </div>

          @if (loading()) {
            <div class="flex items-center gap-2 py-8 text-slate-500">
              <i class="pi pi-spin pi-spinner"></i>
              <span>Cargando perfil...</span>
            </div>
          } @else if (profile(); as user) {
            <div class="mb-8">
              <h4 class="text-sm font-semibold text-slate-700 mb-1">Foto de perfil</h4>
              <p class="text-xs text-slate-500 mb-3">
                Sube una imagen desde tu equipo, ajústala en el círculo y se guardará de inmediato.
              </p>
              <app-avatar-uploader
                [avatarUrl]="avatarPreviewUrl()"
                [disabled]="avatarUploading()"
                [buttonLabel]="avatarPreviewUrl() ? 'Cambiar foto' : 'Subir foto'"
                (avatarChange)="onAvatarChange($event)"
              />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mt-4">
              <div class="flex flex-col gap-1">
                <p-floatlabel variant="on">
                  <input
                    pInputText
                    id="profileName"
                    [value]="user.name || ''"
                    fluid
                    readonly
                    disabled
                    class="bg-slate-50"
                  />
                  <label for="profileName">Nombre</label>
                </p-floatlabel>
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel variant="on">
                  <input
                    pInputText
                    id="profileLastname"
                    [value]="user.lastname || ''"
                    fluid
                    readonly
                    disabled
                    class="bg-slate-50"
                  />
                  <label for="profileLastname">Apellido paterno</label>
                </p-floatlabel>
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel variant="on">
                  <input
                    pInputText
                    id="profileSurname"
                    [value]="user.surname || ''"
                    fluid
                    readonly
                    disabled
                    class="bg-slate-50"
                  />
                  <label for="profileSurname">Apellido materno</label>
                </p-floatlabel>
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel variant="on">
                  <input
                    pInputText
                    id="profileEmail"
                    [value]="user.email || ''"
                    fluid
                    readonly
                    disabled
                    class="bg-slate-50"
                  />
                  <label for="profileEmail">Correo electrónico</label>
                </p-floatlabel>
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel variant="on">
                  <input
                    pInputText
                    id="profilePhone"
                    [value]="user.phone || ''"
                    fluid
                    readonly
                    disabled
                    class="bg-slate-50"
                  />
                  <label for="profilePhone">Teléfono</label>
                </p-floatlabel>
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel variant="on">
                  <input
                    pInputText
                    id="profileJobTitle"
                    [value]="user.jobTitle || ''"
                    fluid
                    readonly
                    disabled
                    class="bg-slate-50"
                  />
                  <label for="profileJobTitle">Cargo</label>
                </p-floatlabel>
              </div>
            </div>

            <div class="mt-8 pt-6 border-t border-slate-200 max-w-xl">
              <h4 class="text-sm font-semibold text-slate-700 mb-1">Firma de correo</h4>
              <p class="text-xs text-slate-500 mb-3">
                Se anexará automáticamente al enviar correos desde la bitácora de ventas (seguimiento CXC).
                Las firmas se configuran en Empresa → Firmas.
              </p>
              <p-select
                [options]="signatureOptions()"
                optionLabel="label"
                optionValue="value"
                [ngModel]="selectedSignatureId()"
                (ngModelChange)="onSignatureChange($event)"
                [disabled]="signatureSaving()"
                placeholder="Selecciona una firma"
                styleClass="w-full"
                [showClear]="false"
              />
              @if (signatureSaving()) {
                <p class="mt-2 text-xs text-slate-500"><i class="pi pi-spin pi-spinner mr-1"></i>Guardando…</p>
              }
            </div>
          } @else {
            <p class="text-slate-500 py-4">No se pudo cargar la información del perfil.</p>
          }
        </p-tabpanel>

        <p-tabpanel value="password">
          <div class="mt-4 mb-4">
            <h3 class="text-lg font-semibold text-slate-900">Cambiar contraseña</h3>
            <p class="text-sm text-slate-500">
              Ingresa tu contraseña actual y la nueva contraseña dos veces para confirmarla.
            </p>
          </div>

          <form [formGroup]="passwordForm" (ngSubmit)="onChangePassword()">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-4 items-start">
              <div class="grid grid-cols-1 gap-8">
                <div class="flex flex-col gap-1">
                  <p-floatlabel variant="on">
                    <p-inputgroup class="w-full">
                      <input
                        pInputText
                        id="currentPassword"
                        [type]="currentPasswordVisible() ? 'text' : 'password'"
                        formControlName="currentPassword"
                        [maxlength]="50"
                        fluid
                        autocomplete="current-password"
                        [invalid]="
                          (passwordForm.get('currentPassword')?.invalid ?? false) &&
                          (passwordForm.get('currentPassword')?.touched ?? false)
                        "
                      />
                      <p-inputgroup-addon>
                        <button
                          type="button"
                          class="p-0 border-0 bg-transparent cursor-pointer text-slate-500 hover:text-slate-700"
                          (click)="toggleCurrentPasswordVisible()"
                          [attr.aria-label]="
                            currentPasswordVisible() ? 'Ocultar contraseña' : 'Ver contraseña'
                          "
                        >
                          <i
                            [class]="
                              currentPasswordVisible() ? 'pi pi-eye-slash' : 'pi pi-eye'
                            "
                          ></i>
                        </button>
                      </p-inputgroup-addon>
                    </p-inputgroup>
                    <label for="currentPassword">Contraseña actual</label>
                  </p-floatlabel>
                  @if (
                    passwordForm.get('currentPassword')?.hasError('required') &&
                    passwordForm.get('currentPassword')?.touched
                  ) {
                    <small class="text-red-600">La contraseña actual es requerida</small>
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <p-floatlabel variant="on">
                    <p-inputgroup class="w-full">
                      <input
                        pInputText
                        id="newPassword"
                        [type]="newPasswordVisible() ? 'text' : 'password'"
                        formControlName="newPassword"
                        [maxlength]="50"
                        fluid
                        autocomplete="new-password"
                        [invalid]="
                          (passwordForm.get('newPassword')?.invalid ?? false) &&
                          (passwordForm.get('newPassword')?.touched ?? false)
                        "
                      />
                      <p-inputgroup-addon>
                        <button
                          type="button"
                          class="p-0 border-0 bg-transparent cursor-pointer text-slate-500 hover:text-slate-700"
                          (click)="toggleNewPasswordVisible()"
                          [attr.aria-label]="
                            newPasswordVisible() ? 'Ocultar contraseña' : 'Ver contraseña'
                          "
                        >
                          <i [class]="newPasswordVisible() ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
                        </button>
                      </p-inputgroup-addon>
                    </p-inputgroup>
                    <label for="newPassword">Nueva contraseña</label>
                  </p-floatlabel>
                  @if (
                    passwordForm.get('newPassword')?.hasError('required') &&
                    passwordForm.get('newPassword')?.touched
                  ) {
                    <small class="text-red-600">La nueva contraseña es requerida</small>
                  }
                  @if (
                    passwordForm.get('newPassword')?.hasError('strongPassword') &&
                    passwordForm.get('newPassword')?.touched
                  ) {
                    <small class="text-red-600">
                      La contraseña no cumple todos los requisitos de seguridad
                    </small>
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <p-floatlabel variant="on">
                    <p-inputgroup class="w-full">
                      <input
                        pInputText
                        id="confirmPassword"
                        [type]="confirmPasswordVisible() ? 'text' : 'password'"
                        formControlName="confirmPassword"
                        [maxlength]="50"
                        fluid
                        autocomplete="new-password"
                        [invalid]="
                          ((passwordForm.get('confirmPassword')?.invalid ?? false) ||
                            (passwordForm.hasError('passwordMismatch') ?? false)) &&
                          (passwordForm.get('confirmPassword')?.touched ?? false)
                        "
                      />
                      <p-inputgroup-addon>
                        <button
                          type="button"
                          class="p-0 border-0 bg-transparent cursor-pointer text-slate-500 hover:text-slate-700"
                          (click)="toggleConfirmPasswordVisible()"
                          [attr.aria-label]="
                            confirmPasswordVisible() ? 'Ocultar contraseña' : 'Ver contraseña'
                          "
                        >
                          <i
                            [class]="
                              confirmPasswordVisible() ? 'pi pi-eye-slash' : 'pi pi-eye'
                            "
                          ></i>
                        </button>
                      </p-inputgroup-addon>
                    </p-inputgroup>
                    <label for="confirmPassword">Confirmar nueva contraseña</label>
                  </p-floatlabel>
                  @if (
                    passwordForm.get('confirmPassword')?.hasError('required') &&
                    passwordForm.get('confirmPassword')?.touched
                  ) {
                    <small class="text-red-600">Confirma la nueva contraseña</small>
                  }
                  @if (
                    passwordForm.hasError('passwordMismatch') &&
                    passwordForm.get('confirmPassword')?.touched
                  ) {
                    <small class="text-red-600">Las contraseñas no coinciden</small>
                  }
                </div>

                <div class="flex justify-end">
                  <p-button
                    type="submit"
                    label="Actualizar contraseña"
                    icon="pi pi-lock"
                    [loading]="passwordLoading()"
                    [disabled]="passwordForm.invalid || passwordLoading()"
                  />
                </div>
              </div>

              <aside
                class="rounded-lg border border-slate-200 bg-slate-50 p-5"
                aria-label="Requisitos de la contraseña"
              >
                <div class="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <h4 class="text-sm font-semibold text-slate-900">Requisitos de seguridad</h4>
                    <p class="text-xs text-slate-500 mt-1">
                      La nueva contraseña debe cumplir todos los puntos.
                    </p>
                  </div>
                  @if (allPasswordRequirementsMet()) {
                    <span
                      class="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-700"
                    >
                      <i class="pi pi-check-circle text-xs"></i>
                      Completo
                    </span>
                  }
                </div>

                <ul class="space-y-3">
                  @for (req of passwordRequirements(); track req.id) {
                    <li class="flex items-center gap-3 text-sm">
                      <span
                        class="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                        [class]="
                          req.met
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-slate-200 text-slate-500'
                        "
                      >
                        <i [class]="req.met ? 'pi pi-check text-xs' : 'pi pi-circle text-[0.55rem]'"></i>
                      </span>
                      <span [class]="req.met ? 'text-emerald-800 font-medium' : 'text-slate-600'">
                        {{ req.label }}
                      </span>
                    </li>
                  }
                </ul>
              </aside>
            </div>
          </form>
        </p-tabpanel>
      </p-tabpanels>
    </p-tabs>
  </p-card>
</div>
